import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  cn(
    // Base styles
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "text-sm font-bold font-['Sora']",
    "border-3 border-black",
    "transition-all duration-100 ease-out",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    "shrink-0 [&_svg]:shrink-0",
    "outline-none focus-visible:ring-2 focus-visible:ring-[#3F5EF4] focus-visible:ring-offset-2",
    // Push-down effect
    "active:translate-x-1 active:translate-y-1 active:shadow-none"
  ),
  {
    variants: {
      variant: {
        // Primary (Yellow) - Main CTA
        default: cn(
          "bg-[#FFDC00] text-black",
          "shadow-[4px_4px_0_0_#000000]",
          "hover:bg-[#FFE940] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#000000]"
        ),
        // Secondary (Pink)
        secondary: cn(
          "bg-[#FF73A9] text-black",
          "shadow-[4px_4px_0_0_#000000]",
          "hover:bg-[#FF8DB8] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#000000]"
        ),
        // Accent (Blue)
        accent: cn(
          "bg-[#3F5EF4] text-white",
          "shadow-[4px_4px_0_0_#000000]",
          "hover:bg-[#5570F6] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#000000]"
        ),
        // Success (Green)
        success: cn(
          "bg-[#00EB90] text-black",
          "shadow-[4px_4px_0_0_#000000]",
          "hover:bg-[#33F0A6] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#000000]"
        ),
        // Outline (White with black border)
        outline: cn(
          "bg-white text-black",
          "shadow-[4px_4px_0_0_#000000]",
          "hover:bg-[#FFFBF0] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#000000]"
        ),
        // Dark (Black)
        dark: cn(
          "bg-black text-white",
          "shadow-[4px_4px_0_0_#333333]",
          "hover:bg-[#1A1A1A] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#333333]"
        ),
        // Destructive (Red)
        destructive: cn(
          "bg-[#FF4444] text-white",
          "shadow-[4px_4px_0_0_#000000]",
          "hover:bg-[#FF5555] hover:-translate-x-0.5 hover:-translate-y-0.5",
          "hover:shadow-[6px_6px_0_0_#000000]"
        ),
        // Ghost (No shadow, minimal)
        ghost: cn(
          "bg-transparent text-black border-transparent shadow-none",
          "hover:bg-[#FFDC00] hover:border-black hover:shadow-[4px_4px_0_0_#000000]"
        ),
        // Link
        link: cn(
          "text-[#3F5EF4] border-transparent shadow-none",
          "underline-offset-4 hover:underline"
        ),
      },
      size: {
        // Pill shape (100px radius) for default sizes
        default: "h-11 px-6 py-2 rounded-full",
        sm: "h-9 px-4 rounded-full text-xs",
        lg: "h-14 px-8 rounded-full text-base",
        xl: "h-16 px-10 rounded-full text-lg",
        // Icon variants with rounded corners (not pill)
        icon: "size-11 rounded-xl",
        "icon-sm": "size-9 rounded-lg",
        "icon-lg": "size-14 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
